import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { GlobalContext } from '../lib/GlobalVar';
import {
    ConsultaAcessoArquivos, SalvaContaSamba, RemoveContaSamba, AssociaContaSamba, SalvaSambaPropria, TestaContaSamba, ListaUsuariosSamba,
} from '../lib/api';
import './arquivosAcessosTI.css';

// Painel da TI na tela "Acesso a Arquivos" (pedido de 22/10/2026): a TI ASSOCIA a cada pessoa o usuario Samba dela -- um usuario que JA
// EXISTE no servidor de arquivos (nada e criado la). O Central so guarda o usuario e a senha (cifrada) para entrar como ele. A pessoa
// ve exatamente as pastas que aquele usuario ve; SEM usuario associado = SEM acesso nenhum. Na linha da pessoa a TI escolhe um usuario
// ja cadastrado ou informa um que ainda nao esta na lista (testa a conexao, cadastra e associa de uma vez). A senha nunca volta para
// a tela. Regras em backend/src/services/acessoArquivos.js.

const CONTA_VAZIA = { id: null, rotulo: '', usuario: '', dominio: '', host: '', senha: '' };
const NOVO = '__novo__';
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const limpaHost = (h) => String(h || '').trim().replace(/^\\+/, '').replace(/\\+$/, '');
const hostPlausivel = (h) => /^[A-Za-z0-9][A-Za-z0-9._-]{1,79}$/.test(limpaHost(h));
const nomeConta = (c) => (c.rotulo && c.rotulo.toLowerCase() !== String(c.usuario).toLowerCase() ? `${c.rotulo} · ${c.usuario}` : c.usuario);

export default function ArquivosAcessosTI({ onFechar }) {
    const { user } = useContext(GlobalContext);
    const [dados, setDados] = useState(null);
    const [erro, setErro] = useState('');
    const [form, setForm] = useState(null);       // a conta que esta sendo criada/alterada
    const [salvando, setSalvando] = useState(false);
    const [testando, setTestando] = useState('');  // id da conta (ou 'form') em teste
    const [teste, setTeste] = useState(null);      // { quem, ok, shares, Error }
    const [busca, setBusca] = useState('');
    const [soSemAcesso, setSoSemAcesso] = useState(false);
    const [gravando, setGravando] = useState(null); // id da pessoa que esta gravando
    const [sug, setSug] = useState({ usuarios: [], erro: '', carregando: false, host: '' }); // os usuarios que ja existem no servidor digitado

    const carregar = useCallback(async () => {
        const r = await ConsultaAcessoArquivos({ solicitante: user?.email });
        if (!r || r.Error) { setErro((r && r.Error) || 'Falha ao carregar.'); return; }
        setErro('');
        setDados(r);
    }, [user]);
    useEffect(() => { carregar(); }, [carregar]);

    // Ao abrir o formulario e ao digitar o servidor, puxa do proprio servidor os usuarios que JA EXISTEM nele (so os nomes), como sugestao.
    const hostForm = form ? form.host : null; // null = formulario fechado
    const hostPadrao = dados ? dados.servidor.host : '';
    useEffect(() => {
        if (hostForm === null) return undefined;
        const alvo = limpaHost(hostForm) || limpaHost(hostPadrao);
        if (!hostPlausivel(alvo)) { setSug({ usuarios: [], erro: '', carregando: false, host: '' }); return undefined; }
        let vivo = true;
        const t = setTimeout(async () => {
            setSug((s) => ({ ...s, carregando: true, erro: '' }));
            const r = await ListaUsuariosSamba({ solicitante: user?.email, host: alvo });
            if (!vivo) return;
            if (!r || r.Error) setSug({ usuarios: [], erro: (r && r.Error) || 'Falha ao listar os usuários.', carregando: false, host: alvo });
            else setSug({ usuarios: r.usuarios, erro: '', carregando: false, host: r.host });
        }, 600);
        return () => { vivo = false; clearTimeout(t); };
    }, [hostForm, hostPadrao, user]);

    const contas = useMemo(() => (dados ? dados.contas : []), [dados]);
    const pessoas = useMemo(() => (dados ? dados.pessoas : []), [dados]);
    const temAcesso = (p) => !!p.conta_id || p.propria_ativa;

    const lista = useMemo(() => {
        const q = busca.trim().toLowerCase();
        return pessoas.filter((p) => (!soSemAcesso || !temAcesso(p))
            && (!q || `${p.nome} ${p.email} ${p.setor}`.toLowerCase().includes(q)));
    }, [pessoas, busca, soSemAcesso]);
    const semAcesso = pessoas.filter((p) => !temAcesso(p)).length;

    const muda = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

    async function salvarConta() {
        if (!form.usuario.trim()) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Informe o usuário Samba.' });
        if (!form.id && !form.senha) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Informe a senha da conta.' });
        setSalvando(true);
        const r = await SalvaContaSamba({ solicitante: user?.email, conta: form });
        setSalvando(false);
        if (!r || r.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: (r && r.Error) || 'Falha ao salvar.' });
        setForm(null);
        carregar();
        Swal.fire({ icon: 'success', title: 'Conta salva', timer: 1000, showConfirmButton: false });
    }

    async function testar(quem, corpo) {
        setTestando(quem);
        setTeste(null);
        const r = await TestaContaSamba({ solicitante: user?.email, ...corpo });
        setTestando('');
        setTeste({ quem, ...(r || { Error: 'Sem resposta.' }) });
    }

    async function excluir(c) {
        const ok = await Swal.fire({ icon: 'warning', title: `Tirar "${nomeConta(c)}" da lista?`, text: 'O usuário continua existindo no servidor; só a senha guardada aqui é apagada. Só dá para tirar sem pessoas associadas.', showCancelButton: true, confirmButtonText: 'Tirar da lista' });
        if (!ok.isConfirmed) return;
        const r = await RemoveContaSamba({ solicitante: user?.email, id: c.id });
        if (!r || r.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: (r && r.Error) || 'Falha ao excluir.' });
        carregar();
    }

    // O usuario Samba da pessoa ainda nao esta na lista: a TI informa usuario e senha de um usuario que JA EXISTE no servidor; o Central
    // confere que o servidor aceita (nao associa uma senha errada), cadastra e associa de uma vez. Se o usuario ja estava cadastrado,
    // usa o que ja existe.
    async function associarNovo(p) {
        const padrao = dados.servidor.host || '';
        const r = await Swal.fire({
            title: `Usuário Samba de ${p.nome}`,
            html: `<p class="aat-dlg-aviso">Escolha um usuário que <b>já existe</b> no servidor de arquivos. Nada é criado lá: o Central só guarda a senha (cifrada) para entrar com ele.</p>
                <label class="aat-dlg-rotulo" for="aat-h">Servidor</label>
                <input id="aat-h" class="swal2-input" value="${esc(limpaHost(padrao))}" placeholder="ex.: 192.168.0.57" autocomplete="off" spellcheck="false">
                <label class="aat-dlg-rotulo" for="aat-u">Usuário Samba <span id="aat-dica" class="aat-dlg-dica"></span></label>
                <input id="aat-u" class="swal2-input" list="aat-lista" placeholder="clique para ver os usuários do servidor" autocomplete="off" spellcheck="false">
                <datalist id="aat-lista"></datalist>
                <label class="aat-dlg-rotulo" for="aat-s">Senha desse usuário</label>
                <input id="aat-s" class="swal2-input" type="password" placeholder="senha no servidor" autocomplete="new-password">`,
            focusConfirm: false, showCancelButton: true, confirmButtonText: 'Testar e associar', cancelButtonText: 'Cancelar', showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
            didOpen: () => {
                // puxa do servidor digitado os usuarios que ja existem nele (so os nomes) e oferece no campo "Usuário"
                const campoH = document.getElementById('aat-h');
                const lista = document.getElementById('aat-lista');
                const dica = document.getElementById('aat-dica');
                let espera = null;
                let rodada = 0;
                const busca = async () => {
                    const alvo = limpaHost(campoH.value) || limpaHost(padrao);
                    const minha = ++rodada;
                    lista.innerHTML = '';
                    if (!hostPlausivel(alvo)) { dica.textContent = ''; return; }
                    dica.textContent = `— buscando em ${alvo}…`;
                    const r = await ListaUsuariosSamba({ solicitante: user?.email, host: alvo });
                    if (minha !== rodada) return; // o servidor ja foi trocado por outro
                    if (!r || r.Error) { dica.textContent = '— ' + ((r && r.Error) || 'não consegui listar') + ' Digite o usuário.'; return; }
                    lista.innerHTML = r.usuarios.map((u) => `<option value="${esc(u.usuario)}">${u.cadastrado ? 'já cadastrado' : ''}</option>`).join('');
                    dica.textContent = `— ${r.total} em ${r.host}: clique no campo`;
                };
                campoH.addEventListener('input', () => { clearTimeout(espera); espera = setTimeout(busca, 600); });
                busca();
            },
            preConfirm: async () => {
                const usuario = document.getElementById('aat-u').value.trim();
                const senha = document.getElementById('aat-s').value;
                // o servidor igual ao padrao fica vazio (a conta acompanha o padrao se ele mudar)
                const digitado = document.getElementById('aat-h').value.trim();
                const host = limpaHost(digitado) === limpaHost(padrao) ? '' : digitado;
                if (!usuario || !senha) { Swal.showValidationMessage('Informe o usuário e a senha.'); return false; }
                const t = await TestaContaSamba({ solicitante: user?.email, conta: { usuario, senha, host, dominio: '' } });
                if (!t || t.Error) { Swal.showValidationMessage((t && t.Error) || 'O servidor não respondeu.'); return false; }
                const s = await SalvaContaSamba({ solicitante: user?.email, conta: { usuario, senha, host, dominio: '' } });
                let contaId = s && s.conta && s.conta.id;
                let jaExistia = false;
                if ((!s || s.Error) && s && s.conta_id) { contaId = s.conta_id; jaExistia = true; }
                else if (!s || s.Error) { Swal.showValidationMessage((s && s.Error) || 'Falha ao cadastrar.'); return false; }
                const a = await AssociaContaSamba({ solicitante: user?.email, user_id: p.id, conta_id: contaId });
                if (!a || a.Error) { Swal.showValidationMessage((a && a.Error) || 'Falha ao associar.'); return false; }
                return { jaExistia, usuario, pastas: (t.shares || []).length };
            },
        });
        if (!r.isConfirmed || !r.value) return;
        await carregar();
        Swal.fire({
            icon: 'success', title: 'Associado',
            text: `${p.nome} agora usa o usuário ${r.value.usuario}${r.value.jaExistia ? ' (já estava cadastrado)' : ''}. Ele enxerga ${r.value.pastas} ${r.value.pastas === 1 ? 'pasta compartilhada' : 'pastas compartilhadas'}.`,
        });
    }

    async function associar(p, contaId) {
        if (contaId === NOVO) return associarNovo(p);
        setGravando(p.id);
        const r = await AssociaContaSamba({ solicitante: user?.email, user_id: p.id, conta_id: contaId || null });
        setGravando(null);
        if (!r || r.Error) { Swal.fire({ icon: 'warning', title: 'Atenção', text: (r && r.Error) || 'Falha ao gravar.' }); return; }
        carregar();
    }

    async function alternaPropria(p) {
        setGravando(p.id);
        const r = await SalvaSambaPropria({ solicitante: user?.email, user_id: p.id, ativa: !p.propria_ativa });
        setGravando(null);
        if (!r || r.Error) { Swal.fire({ icon: 'warning', title: 'Atenção', text: (r && r.Error) || 'Falha ao gravar.' }); return; }
        carregar();
    }

    if (erro) return <div className="card aat"><div className="aat-erro">{erro}</div><button className="btn ghost sm" onClick={onFechar}>Fechar</button></div>;
    if (!dados) return <div className="card aat muted">Carregando…</div>;

    return (
        <section className="card aat" aria-label="Contas e acessos a arquivos (TI)">
            <div className="aat-topo">
                <div>
                    <div className="eyebrow">Somente a TI</div>
                    <h2 className="aat-titulo">Usuário Samba de cada pessoa</h2>
                    <p className="aat-sub">
                        Associe a cada pessoa o <b>usuário Samba que já existe</b> no servidor <span className="mono">\\{dados.servidor.host || '(servidor não configurado)'}</span>.
                        Ela vê as pastas e os arquivos que <b>aquele usuário</b> vê. <b>Sem usuário associado, ela não tem acesso nenhum.</b> Nada é criado no servidor.
                    </p>
                </div>
                <button className="btn ghost sm" onClick={onFechar}>Fechar</button>
            </div>

            <h3 className="aat-h3">1. Quem usa qual usuário Samba <span className="chip">{pessoas.length} pessoas</span> <span className={'chip' + (semAcesso ? '' : ' on')}>{semAcesso} sem acesso</span></h3>
            <p className="muted aat-dica">Na linha da pessoa, escolha um usuário já cadastrado ou <b>«+ Informar usuário Samba…»</b> para um que ainda não está na lista (o Central testa a senha no servidor antes de associar).</p>
            <div className="aat-filtros">
                <input className="input" placeholder="Buscar pessoa ou setor…" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar pessoa ou setor" />
                <label className="aat-check"><input type="checkbox" checked={soSemAcesso} onChange={(e) => setSoSemAcesso(e.target.checked)} /> só quem está sem acesso</label>
            </div>
            <div className="aat-tabela-wrap">
                <table className="preview-table aat-tabela">
                    <thead><tr><th>Pessoa</th><th>Setor</th><th>Usuário Samba</th><th>Situação</th></tr></thead>
                    <tbody>
                        {lista.map((p) => (
                            <tr key={p.id} className={temAcesso(p) ? '' : 'aat-sem'}>
                                <td><b>{p.nome}</b><div className="muted mono aat-mini">{p.email}</div></td>
                                <td>{p.setor || '—'}</td>
                                <td>
                                    <select className="select" value={p.conta_id || ''} disabled={gravando === p.id} onChange={(e) => associar(p, e.target.value)} aria-label={`Usuário Samba de ${p.nome}`}>
                                        <option value="">— sem acesso —</option>
                                        {contas.map((c) => <option key={c.id} value={c.id}>{nomeConta(c)}</option>)}
                                        <option value={NOVO}>+ Informar usuário Samba…</option>
                                    </select>
                                </td>
                                <td>
                                    {p.conta_id ? <span className="chip on">acesso pelo usuário</span>
                                        : p.conta_perdida ? <span className="aat-alerta">o usuário dela foi removido da lista</span>
                                            : p.propria_ativa ? <span className="muted">usa a credencial antiga que ela mesma guardou</span>
                                                : <span className="muted">sem acesso</span>}
                                    {p.propria && (
                                        <button className="btn ghost sm aat-propria" disabled={gravando === p.id} onClick={() => alternaPropria(p)}>
                                            {p.propria_ativa ? 'Desativar credencial antiga' : 'Religar credencial antiga'}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {lista.length === 0 && <tr><td colSpan="4" className="muted aat-vazio">Ninguém com esse filtro.</td></tr>}
                    </tbody>
                </table>
            </div>

            <h3 className="aat-h3">2. Usuários Samba cadastrados <span className="chip">{contas.length}</span></h3>
            <p className="muted aat-dica">São usuários que já existem no servidor; aqui o Central só guarda a senha (cifrada) para entrar como eles. Use para trocar a senha, testar ou tirar um usuário da lista.</p>
            {contas.length > 0 && (
                <div className="aat-tabela-wrap">
                    <table className="preview-table aat-tabela">
                        <thead><tr><th>Apelido</th><th>Usuário Samba</th><th>Servidor</th><th>Pessoas</th><th /></tr></thead>
                        <tbody>
                            {contas.map((c) => (
                                <tr key={c.id}>
                                    <td><b>{c.rotulo}</b>{c.rotulo.toLowerCase() === String(c.usuario).toLowerCase() && <span className="muted"> (sem apelido)</span>}</td>
                                    <td className="mono">{c.dominio ? c.dominio + '\\' : ''}{c.usuario}</td>
                                    <td className="mono">{c.host || <span className="muted">padrão</span>}</td>
                                    <td>{c.pessoas}</td>
                                    <td className="aat-acoes">
                                        <button className="btn ghost sm" disabled={!!testando} onClick={() => testar(c.id, { id: c.id })}>{testando === c.id ? 'Testando…' : 'Testar'}</button>
                                        <button className="btn ghost sm" onClick={() => { setTeste(null); setForm({ ...CONTA_VAZIA, ...c, senha: '' }); }}>Alterar</button>
                                        <button className="btn ghost sm" onClick={() => excluir(c)}>Tirar da lista</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {contas.length === 0 && !form && <p className="muted">Nenhum usuário cadastrado ainda: na lista acima, escolha «+ Informar usuário Samba…» na linha da pessoa.</p>}

            {teste && (
                <div className={'aat-teste' + (teste.Error ? ' erro' : '')} role="status">
                    {teste.Error
                        ? teste.Error
                        : <>Conectou como <b className="mono">{teste.usuario}</b> em <span className="mono">\\{teste.host}</span>. Ela enxerga {teste.shares.length} {teste.shares.length === 1 ? 'pasta compartilhada' : 'pastas compartilhadas'}:
                            {' '}<span className="aat-shares">{teste.shares.length ? teste.shares.join(' · ') : '(nenhuma)'}</span></>}
                </div>
            )}

            {form ? (
                <div className="aat-form">
                    <div className="aat-form-grade">
                        <div className="field"><label>Usuário Samba <span className="muted">(que já existe no servidor)</span></label>
                            <input className="input mono" list="aat-lista-form" value={form.usuario} onChange={muda('usuario')} placeholder="ex.: michele.financeiro" autoComplete="off" />
                            <datalist id="aat-lista-form">{sug.usuarios.map((u) => <option key={u.usuario} value={u.usuario}>{u.cadastrado ? 'já cadastrado' : ''}</option>)}</datalist>
                            <small className="muted">{sug.carregando ? 'Buscando os usuários do servidor…' : (sug.erro ? sug.erro + ' Digite o usuário.' : (sug.usuarios.length ? `${sug.usuarios.length} usuários em ${sug.host}: clique no campo para ver a lista.` : ''))}</small></div>
                        <div className="field"><label>Senha {form.id && <span className="muted">(vazio = manter)</span>}</label><input className="input" type="password" value={form.senha} onChange={muda('senha')} autoComplete="new-password" placeholder={form.id ? 'manter a atual' : 'senha desse usuário no servidor'} /></div>
                        <div className="field"><label>Apelido <span className="muted">(opcional)</span></label><input className="input" value={form.rotulo} onChange={muda('rotulo')} maxLength={60} placeholder="ex.: Financeiro (vazio = o usuário)" /></div>
                        <div className="field"><label>Servidor <span className="muted">(vazio = padrão)</span></label><input className="input mono" value={form.host} onChange={muda('host')} placeholder="192.168.0.57" /></div>
                        <div className="field"><label>Domínio <span className="muted">(vazio = sem)</span></label><input className="input" value={form.dominio} onChange={muda('dominio')} placeholder="deixe vazio em servidor sem AD" /></div>
                    </div>
                    <div className="aat-form-botoes">
                        <button className="btn" onClick={salvarConta} disabled={salvando}>{salvando ? 'Salvando…' : (form.id ? 'Salvar alterações' : 'Cadastrar')}</button>
                        <button className="btn ghost" disabled={!!testando || (!form.id && !form.senha)} onClick={() => testar('form', form.id && !form.senha ? { id: form.id } : { conta: form })}>{testando === 'form' ? 'Testando…' : 'Testar antes de salvar'}</button>
                        <button className="btn ghost" onClick={() => setForm(null)}>Cancelar</button>
                    </div>
                </div>
            ) : (
                <button className="btn ghost" onClick={() => { setTeste(null); setForm({ ...CONTA_VAZIA, dominio: '' }); }}>Cadastrar usuário Samba</button>
            )}
        </section>
    );
}
